/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useCallback, useEffect } from 'react';
import { css, jsx } from '@emotion/react';

import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

import Button from '@mui/material/Button';
import Select from '../common/Select';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Text from '@mui/material/Typography';

import { useUserQuery } from '../../actions/UserActions';
import { useMeetingActions } from '../../actions/MeetingActions';

import { SPEAKER_OPTIONS, MEETING_SOURCES } from '../../constants';

import { useTranslation } from 'react-i18next';

function MeetingSpeakerOptions({ allowAuto, meetingBotSpeakers, setMeetingBotSpeakers }) {
  const { t } = useTranslation();

  let options = [];
  if (allowAuto) {
  options.push({ 
      value: 'auto',
      label: t('Automatisk'),
      description: t('Klang försöker automatiskt identifiera talarna baserat på både mötesdeltagare och röster.'),
    });
  }

  options = options.concat([
    {
      value: true,
      label: t('Använd deltagarlista för talare'),
      description: t('AI-sekreteraren identifierar talare från deltagarlistan på mötet.'),
    },
    {
      value: false,
      label: t('Identifiera talare endast från rösten'),
      description: t('AI-sekreteraren använder endast rösterna för att identifiera talare. Bör användas vid hybridmöten.'),
    },
  ]);

  return (
    <FormControl component="fieldset">
      <RadioGroup
        value={meetingBotSpeakers}
        onChange={(e) => setMeetingBotSpeakers(e.target.value)}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={
              <div>
                <Text
                  css={css`
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 0.15px;
                    line-height: 1.5;
                  `}
                >
                  {option.label}
                </Text>
                <Text
                  css={css`
                    color: rgba(0, 0, 0, 0.6);
                    font-size: 12px;
                  `}
                >
                  {option.description}
                </Text>
              </div>
            }
            css={css`margin-bottom: 1rem;`}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

export default function RunSpeakerIdMenu({
  meetingId,
  source,
  meetingBotSpeakers: defaultMeetingBotSpeakers,
  closeSettings,
  toSettings,
}) {
  const { t } = useTranslation();
  const [numSpeakers, setNumSpeakers] = useState(0);
  const allowAuto = source === 'google';
  const [meetingBotSpeakers, setMeetingBotSpeakers] = useState(
    (allowAuto) ? 'auto' : defaultMeetingBotSpeakers
  );
  const isMeeting = MEETING_SOURCES.includes(source);

  const { user } = useUserQuery();
  const workspace = user?.workspace;
  const allowHybridSettings = workspace?.allow_hybrid_meeting;

  const { runSpeakerIdAsync } = useMeetingActions(meetingId);

  const handleRun = useCallback(async () => {
    if ( !isMeeting || meetingBotSpeakers === 'auto') {
      await runSpeakerIdAsync({ numSpeakers });
    } else {
      runSpeakerIdAsync({
        numSpeakers,
        meetingBotSpeakers: meetingBotSpeakers==='true',
        overrideMeetingBotSpeakers: true
      });
    }
    closeSettings();
  }, [runSpeakerIdAsync, numSpeakers, meetingBotSpeakers, isMeeting, closeSettings]);

  return (
    <React.Fragment>
      <DialogTitle
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          justify-content: space-between;
        `}
      >
        <div css={css`display: flex; align-items: center; gap: 8px;`}>
          <IconButton
            edge="start"
            onClick={toSettings}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Text variant="subtitle1">{t('Kör om talaridentifikation')}</Text>
        </div>
        <IconButton
          onClick={closeSettings}
          aria-label="close"
          css={css`
            // padding: 4px;
          `}
          variant="outlined"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent
        css={css`
          gap: 16px;
          display: flex;
          flex-direction: column;
        `}
      >
        <div css={css`gap: 8px; display: flex; flex-direction: column;`}>
          <DialogContentText>
            {t('Välj antal talare')}
          </DialogContentText>
          <Select
            options={SPEAKER_OPTIONS}
            value={numSpeakers}
            onChange={(e) => setNumSpeakers(e.target.value)}
            size="small"
            css={css`
              width: 100%;
              margin-top: 0px;
            `}
          />
        </div>
        { isMeeting && allowHybridSettings && (
        <div css={css`gap: 16px; display: flex; flex-direction: column;`}>
          <DialogContentText>
            {t('Inställningar för mötesdeltagare')}
          </DialogContentText>
          <MeetingSpeakerOptions
            allowAuto={allowAuto}
            meetingBotSpeakers={meetingBotSpeakers}
            setMeetingBotSpeakers={setMeetingBotSpeakers}
          />
        </div>
        )}
      </DialogContent>

      <DialogActions
        css={css`
        `}
      >
        <Button onClick={toSettings}>{t('Avbryt')}</Button>
        <Button variant="contained" onClick={handleRun}>{t('Kör om talaridentifikation')}</Button>
      </DialogActions>
    </React.Fragment>
  );
}
